import $ from 'jquery';
import { type ComponentProps } from 'react';
import type React from 'react';
import { Provider, useSelector } from 'react-redux';
import ReactDOM from 'react-dom';

import * as SocrataVisualizations from 'common/visualizations';
import MostRecentlyUsed from 'common/most_recently_used';
import 'common/notifications/main';
import 'common/site_wide';
import AssetActionBar from 'common/components/AssetActionBar';
import PersonalizedStoryCreator from 'view/PersonalizedStoryCreator';

import Environment from 'StorytellerEnvironment';
import migrateStoryData from 'lib/migrateStoryData';
import 'lib/components/block-component-renderers';
import 'lib/components/Modal';
import { StoryCopierRenderer } from 'editor/renderers/StoryCopierRenderer';
import { setupWindowSizeListener } from 'services/WindowSizeService';
import PresentationMode from './PresentationMode';
import AssetActionItem from './components/AssetActionItem';
import ScheduledReportButton from './components/ScheduledReportButton';
import { getView } from 'common/core/views_service';
import { getCurrentUser } from 'common/current_user';
import { FeatureFlags } from '@socrata/platform/feature-flags';
import PdfCreatedAtItem from './components/PdfCreatedAtItem';
import EmbedPersonalizedResetButton from './components/EmbedPersonalizedResetButton';
import { type View } from 'common/types/view';
import type InteractiveUser from 'common/types/users/interactiveUser';
import { selectWindowSizeClass, selectUnusedWindowSizeClasses } from '../store/reducers/WindowSizeReducer';
/**
 * Redux store imports
 */
import { StorytellerReduxStore, StorytellerReduxStoreFactory } from 'store/StorytellerReduxStore';
import { createStory } from 'store/TopLevelActions';
import { populateGlobalFilter, populateDatasetMetadata } from 'store/reducers/DataSourceReducer';
import { getIsPersonalizedVersion } from 'store/selectors/PersonalizedStorySelectors';
import { DerivedReportSelector } from './components/DerivedReportSelector/DerivedReportSelector';
import { ViewStory } from './components/ViewStory';
import { ActionsConfigBanner } from './components/ActionsConfigBanner';
import { EmbedHeader } from './components/EmbedHeader';
import { getAllFilterWhereClauses, getAllFilterMetadata } from 'store/reducers/GridFilterReducer';
import { isTablePrintingImprovementForScheduledSendEnabled } from 'common/visualizations/helpers/VifSelectors';

declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: any;
  }
}

interface AABWrapperProps {
  user: InteractiveUser;
  view: View;
}

const renderAssetActionItem = async () => {
  const currentUser = getCurrentUser();
  const currentView = await getView(Environment.STORY_UID);

  if (
    $('.btn-watch-asset-button').length > 0 &&
    FeatureFlags.valueOrDefault('enable_screenshot_subscriptions') &&
    FeatureFlags.valueOrDefault('enable_embedded_schedule_send') &&
    Environment.RENDER_EMBEDDED_SCHEDULE_SEND
  ) {
    ReactDOM.render(
      <AssetActionItem user={currentUser} view={currentView} />,
      document.getElementsByClassName('btn-watch-asset-button')[0],
    );
  }
};

const renderScheduledSendButton = async () => {
  const currentUser = getCurrentUser();
  const currentView = await getView(Environment.STORY_UID);

  if (
    $('#btn-send-scheduled-report').length > 0 &&
    FeatureFlags.valueOrDefault('enable_report_scheduled_delivery', false) &&
    FeatureFlags.valueOrDefault('enable_embedded_schedule_send', false) &&
    Environment.RENDER_EMBEDDED_SCHEDULE_SEND
  ) {
    ReactDOM.render(
      <Provider store={StorytellerReduxStoreFactory()}>
        <ScheduledReportButton user={currentUser} view={currentView} />
      </Provider>,
      document.getElementById('btn-send-scheduled-report'),
    );
  }
};

const renderPdfCreatedAtItem = async () => {
  if (Environment.RENDER_PDF_CREATED_TIMESTAMP) {
    const currentUser = await getCurrentUser();
    if ($('#pdf-created-at-timestamp').length > 0) {
      ReactDOM.render(
        <PdfCreatedAtItem user={currentUser} />,
        document.getElementById('pdf-created-at-timestamp'),
      );
    }
  }
};

const AABWrapper: React.FC<AABWrapperProps> = (props) => {
  const { user, view } = props;
  const isPersonalizedVersion = useSelector(getIsPersonalizedVersion);
  const allFilterWhereClauses = useSelector(getAllFilterWhereClauses);
  const allFilterMetadata = useSelector(getAllFilterMetadata);

  const relatedStories = Environment.RELATED_STORIES;
  const hasSelectableReports =
    relatedStories && relatedStories.derivedStories.length + (relatedStories.defaultStory ? 1 : 0) > 1;

  const titleOverride = hasSelectableReports ? (
    <DerivedReportSelector view={view} relatedStories={relatedStories} />
  ) : undefined;

  return (
    <AssetActionBar
      user={user}
      view={view}
      personalizedStory={isPersonalizedVersion}
      titleOverride={titleOverride}
      filterWhereClauses={allFilterWhereClauses}
      {...(isTablePrintingImprovementForScheduledSendEnabled() && { filterMetadata: allFilterMetadata })}
    />
  );
};

const renderResetPersonalizationButton = () => {
  if (Environment.RENDER_EMBEDDED_PERSONALIZED_RESET) {
    if ($('.btn-reset-personalization').length > 0) {
      ReactDOM.render(
        <Provider store={StorytellerReduxStore}>
          <EmbedPersonalizedResetButton />
        </Provider>,
        document.getElementsByClassName('btn-reset-personalization')[0],
      );
    }
  }
};

function _renderStory(props: ComponentProps<typeof ViewStory>) {
  const { onFinishRenderCallback } = props;

  ReactDOM.render(
    <Provider store={StorytellerReduxStore}>
      <ViewStory onFinishRenderCallback={onFinishRenderCallback} />
    </Provider>,
    document.getElementsByClassName('user-story')[0],
  );
}

$(function () {
  /** Do not read directly from STORY_DATA! Instead, prefer reading from the Redux Store. */
  const STORY_DATA = Environment.STORY_DATA!;
  const PERSONALIZED_STORY_DATA = Environment.PERSONALIZED_STORY_DATA!;
  migrateStoryData(STORY_DATA).then(() => {
    renderAssetActionItem();
    renderScheduledSendButton();
    renderPdfCreatedAtItem();

    /**
     * The `setup` function is in a JS file and defines two arguments.
     * JS lets you not pass in any arguments, while TS doesn't. We need to
     * fix the signature of the `setup` function in order to remove this.
     */
    // @ts-expect-error
    SocrataVisualizations.views.RowInspector.setup();

    setupWindowSizeListener();

    const $userStoryContainer = $('.user-story-container');
    const $userStory = $('.user-story');

    function _resizeVizOnPrint() {
      $('.socrata-visualization-renderer').trigger('SOCRATA_VISUALIZATION_INVALIDATE_SIZE');
    }

    // This doesn't work in FF, but in Chrome it tells the visualizations to resize themselves
    // when Ctrl+P is pressed or Print is chosen from the browser menu.
    if (
      typeof window.matchMedia === 'function' &&
      typeof window.matchMedia('print')?.addEventListener === 'function'
    ) {
      window.matchMedia('print').addEventListener('change', (e) => {
        if (e.matches) {
          setTimeout(_resizeVizOnPrint);
        }
      });
    }

    function _applyWindowSizeClass() {
      const state = StorytellerReduxStore.getState();
      const windowSizeClass = selectWindowSizeClass(state);
      const unusedWindowSizeClasses = selectUnusedWindowSizeClasses(state);

      $userStoryContainer.removeClass(unusedWindowSizeClasses.join(' ')).addClass(windowSizeClass);
      $userStory.removeClass(unusedWindowSizeClasses.join(' ')).addClass(windowSizeClass);
    }

    // Update store subscription
    StorytellerReduxStore.subscribe(_applyWindowSizeClass);

    /**
     * Populating the Redux store with the story data in view mode
     *
     * TODO: We should refactor this once we fully transition to Redux.
     * These should be set as the initial state and should not need
     * special actions to populate the store.
     */

    if (
      FeatureFlags.valueOrDefault('enable_saved_state_in_stories') &&
      PERSONALIZED_STORY_DATA?.uid &&
      PERSONALIZED_STORY_DATA.updatedAt &&
      STORY_DATA.updatedAt &&
      new Date(PERSONALIZED_STORY_DATA.updatedAt) > new Date(STORY_DATA.updatedAt)
    ) {
      StorytellerReduxStore.dispatch(createStory(PERSONALIZED_STORY_DATA));
      PersonalizedStoryCreator.loadPersonalizedStory();
      StorytellerReduxStore.dispatch(populateGlobalFilter(PERSONALIZED_STORY_DATA));
    } else {
      StorytellerReduxStore.dispatch(createStory(STORY_DATA));
      StorytellerReduxStore.dispatch(populateGlobalFilter(STORY_DATA));
    }
    // Unclear why when thunks have generic types on them, they don't match AnyAction anymore.
    // Maybe later versions of redux/redux-thunk will fix this. For now, cast to any.
    StorytellerReduxStore.dispatch(populateDatasetMetadata() as any);

    const onFinishRenderCallback = () => {
      // If the url has a hash, the element won't exist until the story is rendered. We can scroll to it now.
      if (window.location.hash) {
        const target = document.getElementById(window.location.hash.substring(1));
        if (target) {
          target.scrollIntoView();
        }
      }
    };

    _renderStory({ onFinishRenderCallback });

    const assetActionBarContainer = document.getElementById('storyteller-asset-action-bar-container');
    if (assetActionBarContainer) {
      const actionsConfigBannerHost = document.createElement('div');
      assetActionBarContainer.insertAdjacentElement('afterend', actionsConfigBannerHost);
      ReactDOM.render(
        <Provider store={StorytellerReduxStore}>
          <ActionsConfigBanner />
        </Provider>,
        actionsConfigBannerHost,
      );
    }

    // PresentationMode must be rendered after the story, but because it has it's own ReactDOM.render,
    // it can't (and probably doesn't need to) go in the normal onFinishedRender callback.
    PresentationMode();

    // This can only happen after presentation mode buttons are rendered
    renderResetPersonalizationButton();

    // Init window size
    _applyWindowSizeClass();

    const aabContainer = document.getElementById('storyteller-asset-action-bar-container');
    if (
      Environment.RENDER_ASSET_ACTION_BAR &&
      Environment.CURRENT_USER &&
      Environment.CORE_VIEW &&
      aabContainer
    ) {
      ReactDOM.render(
        <Provider store={StorytellerReduxStore}>
          <AABWrapper user={Environment.CURRENT_USER} view={Environment.CORE_VIEW} />
        </Provider>,
        aabContainer,
      );

      // @ts-expect-error no implicit any
      new StoryCopierRenderer({
        storyCopierContainerElement: $('#make-a-copy-container'),
      });
    }

    const embedHeaderContainer = document.getElementById('story-embed-header-bar-container');
    if (
      !Environment.RENDER_ASSET_ACTION_BAR &&
      Environment.CURRENT_USER &&
      Environment.CORE_VIEW &&
      embedHeaderContainer
    ) {
      ReactDOM.render(
        <EmbedHeader view={Environment.CORE_VIEW} relatedStories={Environment.RELATED_STORIES} />,
        embedHeaderContainer,
      );
    }

    if (Environment.CURRENT_USER && Environment.STORY_UID) {
      new MostRecentlyUsed({ namespace: `socrata:assets:mru:${Environment.CURRENT_USER.id}` }).add(
        Environment.STORY_UID,
      );
    }
  });
});
